/**
 * Scheduling Service
 *
 * Fetches booking, summary, role-coverage and warning data from the
 * Projects API adapter endpoint which normalises data from the
 * connected resource scheduler (EBScheduler).
 *
 * In demo/tour mode the existing mock data is returned so the UI can
 * be developed without a live backend.  In live mode the real API is called.
 */

import { apiFetch } from '@/lib/api-error-handler';
import { getDataMode, getApiBaseUrl } from '@/lib/data-service';
import {
  mockSchedulingBookings,
  mockSchedulerConnection,
  mockResourceRequests,
  mockSchedulingSummary,
  mockRoleCoverage,
  mockSchedulingWarnings,
  mockTeamBookingStatuses,
  mockDashboardAlerts,
  mockProjectBookingHealth,
  type SchedulingBooking,
  type ResourceRequest,
  type RoleCoverage,
  type SchedulingWarning,
  type TeamMemberBookingStatus,
  type DashboardSchedulingAlert,
  type ProjectBookingHealth,
} from '@/lib/mock-scheduling-data';

// ---------------------------------------------------------------------------
// Response types — resource request list (GET)
// ---------------------------------------------------------------------------

export interface ResourceRequestResponse {
  requestId: string;
  roleName: string;
  hoursRequired: number;
  desiredStartDate: string;
  desiredEndDate?: string;
  priority: string;
  status: string;
  notes?: string;
  raisedAt: string;
  assignedTo?: string;
}

// ---------------------------------------------------------------------------
// Request types — resource request creation
// ---------------------------------------------------------------------------

export interface CreateResourceRequestPayload {
  roleId?: string;
  roleName: string;
  hoursRequired: number;
  desiredStartDate: string; // YYYY-MM-DD
  priority: string; // nice-to-have | important | critical
  notes?: string;
  skills?: Array<{ skillId: string; skillName: string; category: string; requirement: string }>;
}

export interface CreateResourceRequestResponse {
  requestId: string | null;
  status: string;
  message: string | null;
}

// ---------------------------------------------------------------------------
// Response types (mirror the backend SchedulingBookingsResponse DTO)
// ---------------------------------------------------------------------------

export interface SchedulingBookingsResponse {
  connectionStatus: 'connected' | 'not_configured' | 'error';
  schedulerName: string | null;
  bookings: SchedulingBooking[];
  schedulerUrl?: string | null;
}

// ---------------------------------------------------------------------------
// Response types — summary, role coverage, warnings
// ---------------------------------------------------------------------------

export interface UnmetRoleResponse {
  roleId: string;
  roleName: string;
  budgetHours: number;
  scheduledHours: number;
  gapHours: number;
}

export interface SummaryWarningResponse {
  type: string;
  message: string;
}

export interface SchedulingSummaryResponse {
  totalBudgetHours: number;
  scheduledHours: number;
  bookingCoveragePercent: number;
  confirmedBookings: number;
  plannedBookings: number;
  totalBookings: number;
  unmetDemandHours: number;
  unmetDemandRoles: number;
  healthStatus: string;
  unmetRoles: UnmetRoleResponse[];
  warnings: SummaryWarningResponse[];
  bookingsEndDate: string | null;
  projectEndDate: string | null;
}

export interface RoleCoveragePersonResponse {
  personId: string;
  personName: string;
  scheduledHours: number;
  actualHours: number;
  status: string;
}

export interface RoleCoverageItemResponse {
  roleId: string;
  roleName: string;
  budgetHours: number;
  scheduledHours: number;
  actualHours: number;
  remainingHours: number;
  coveragePercent: number;
  status: string;
  hasPendingRequest: boolean;
  people: RoleCoveragePersonResponse[];
}

export interface RoleCoverageResponse {
  roles: RoleCoverageItemResponse[];
}

export interface SchedulingWarningResponse {
  id: string;
  type: string;
  severity: string;
  message: string;
  roleName?: string;
  gapHours?: number;
}

export interface SchedulingWarningsResponse {
  warnings: SchedulingWarningResponse[];
}

// ---------------------------------------------------------------------------
// Request/response types — booking update
// ---------------------------------------------------------------------------

export interface UpdateBookingPayload {
  startTime?: string;
  endTime?: string;
  durationHours?: number;
  status?: string;
  priority?: string;
  bookingNotes?: string;
  resourceId?: number;
}

// ---------------------------------------------------------------------------
// Service function
// ---------------------------------------------------------------------------

/**
 * Fetch normalised scheduling bookings for a project.
 *
 * - demo / tour → returns mock data immediately
 * - live        → calls GET /api/projects/{projectId}/scheduling/bookings
 */
export async function fetchSchedulingBookings(
  projectId: string,
): Promise<SchedulingBookingsResponse> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    // Simulate a small network delay so loading states are visible during dev
    await new Promise((resolve) => setTimeout(resolve, 300));
    return {
      connectionStatus: mockSchedulerConnection.status === 'connected' ? 'connected' : 'not_configured',
      schedulerName: mockSchedulerConnection.displayName,
      bookings: mockSchedulingBookings,
      schedulerUrl: mockSchedulerConnection.schedulerUrl ?? null,
    };
  }

  // Live mode — call the real backend adapter endpoint
  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/bookings`;
  const data = await apiFetch<SchedulingBookingsResponse>(url);
  return data;
}

// ---------------------------------------------------------------------------
// Create resource request
// ---------------------------------------------------------------------------

/**
 * Create a resource request (unallocated demand booking) in the connected scheduler.
 *
 * - demo / tour → simulates success after a short delay
 * - live        → calls POST /api/projects/{projectId}/scheduling/requests
 */
/**
 * Fetch all resource requests for a project.
 *
 * - demo / tour → returns mock data immediately
 * - live        → calls GET /api/projects/{projectId}/scheduling/requests
 */
export async function fetchResourceRequests(
  projectId: string,
): Promise<ResourceRequestResponse[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    // Map mock data to the response shape
    return mockResourceRequests.map((r: ResourceRequest) => ({
      requestId: r.requestId,
      roleName: r.roleName,
      hoursRequired: r.hoursRequired,
      desiredStartDate: r.startDate,
      desiredEndDate: r.endDate,
      priority: r.priority,
      status: r.status,
      notes: r.notes,
      raisedAt: r.raisedAt,
      assignedTo: r.assignedTo,
    }));
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/requests`;
  const data = await apiFetch<ResourceRequestResponse[]>(url);
  return data;
}

// ---------------------------------------------------------------------------
// Summary, Role Coverage, Warnings
// ---------------------------------------------------------------------------

export async function fetchSchedulingSummary(
  projectId: string,
): Promise<SchedulingSummaryResponse> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return {
      totalBudgetHours: mockSchedulingSummary.totalBudgetHours,
      scheduledHours: mockSchedulingSummary.totalScheduledHours,
      bookingCoveragePercent: mockSchedulingSummary.coveragePercent,
      confirmedBookings: mockSchedulingSummary.confirmedBookings,
      plannedBookings: mockSchedulingSummary.plannedBookings,
      totalBookings: mockSchedulingSummary.totalBookings,
      unmetDemandHours: mockSchedulingSummary.unmetRoles.reduce((sum, r) => sum + r.gapHours, 0),
      unmetDemandRoles: mockSchedulingSummary.unmetRoles.length,
      healthStatus: mockSchedulingSummary.healthStatus,
      unmetRoles: mockSchedulingSummary.unmetRoles,
      warnings: mockSchedulingSummary.warnings.map((w) => ({ type: w.type, message: w.message })),
      bookingsEndDate: mockSchedulingSummary.bookingsEndDate,
      projectEndDate: mockSchedulingSummary.projectEndDate,
    };
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/summary`;
  return await apiFetch<SchedulingSummaryResponse>(url);
}

export async function fetchRoleCoverage(
  projectId: string,
): Promise<RoleCoverage[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockRoleCoverage;
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/role-coverage`;
  const data = await apiFetch<RoleCoverageResponse>(url);
  return data.roles.map((r) => ({
    roleId: r.roleId,
    roleName: r.roleName,
    budgetHours: r.budgetHours,
    scheduledHours: r.scheduledHours,
    actualHours: r.actualHours,
    remainingHours: r.remainingHours,
    coveragePercent: r.coveragePercent,
    status: r.status as RoleCoverage['status'],
    hasPendingRequest: r.hasPendingRequest,
    people: r.people.map((p) => ({
      personId: p.personId,
      personName: p.personName,
      scheduledHours: p.scheduledHours,
      actualHours: p.actualHours,
    })),
  }));
}

export async function fetchSchedulingWarnings(
  projectId: string,
): Promise<SchedulingWarning[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockSchedulingWarnings;
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/warnings`;
  const data = await apiFetch<SchedulingWarningsResponse>(url);
  return data.warnings.map((w) => ({
    type: w.type as SchedulingWarning['type'],
    severity: w.severity as SchedulingWarning['severity'],
    message: w.message,
  }));
}

// ---------------------------------------------------------------------------
// Booking update / cancel
// ---------------------------------------------------------------------------

export async function updateBooking(
  projectId: string,
  bookingId: string,
  payload: UpdateBookingPayload,
): Promise<SchedulingBooking> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 500));
    const existing = mockSchedulingBookings.find((b) => b.bookingId === bookingId);
    return existing ?? mockSchedulingBookings[0];
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/bookings/${bookingId}`;
  return await apiFetch<SchedulingBooking>(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function cancelBooking(
  projectId: string,
  bookingId: string,
): Promise<void> {
  await updateBooking(projectId, bookingId, { status: 'cancelled' });
}

export async function createResourceRequest(
  projectId: string,
  payload: CreateResourceRequestPayload,
  options?: { useDemandFlow?: boolean },
): Promise<CreateResourceRequestResponse> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 800));
    return {
      requestId: `mock-${Date.now()}`,
      status: 'Pending',
      message: `Resource request created for ${payload.roleName}.`,
    };
  }

  let url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/requests`;
  if (options?.useDemandFlow) {
    url += '?useDemandFlow=true';
  }
  const data = await apiFetch<CreateResourceRequestResponse>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return data;
}

// ---------------------------------------------------------------------------
// Team booking statuses
// ---------------------------------------------------------------------------

export async function fetchTeamBookingStatuses(
  projectId: string,
): Promise<TeamMemberBookingStatus[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockTeamBookingStatuses;
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/team/booking-statuses`;
  const data = await apiFetch<TeamMemberBookingStatus[]>(url);
  return data;
}

// ---------------------------------------------------------------------------
// Dashboard scheduling alerts
// ---------------------------------------------------------------------------

export async function fetchSchedulingAlerts(): Promise<DashboardSchedulingAlert[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockDashboardAlerts;
  }

  const url = `${getApiBaseUrl()}/dashboard/scheduling-alerts`;
  const data = await apiFetch<DashboardSchedulingAlert[]>(url);
  return data;
}

// ---------------------------------------------------------------------------
// Project scheduling health (bulk)
// ---------------------------------------------------------------------------

export async function fetchSchedulingHealth(): Promise<ProjectBookingHealth[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockProjectBookingHealth;
  }

  const url = `${getApiBaseUrl()}/projects/scheduling-health`;
  const data = await apiFetch<ProjectBookingHealth[]>(url);
  return data;
}
